import React, { useMemo, useRef, useState } from 'react';
import './styles/medication-profile-modal.css';

import MedicationProfileRecords from './MedicationProfileRecords';
import { MedicationProfileStates, MedicationProfileSteps } from './types';
import MedicationProfileDetail from './MedicationProfileDetail';
import { useDeleteMedProfiles } from './hook';
import { MedProfile } from './hook/types';
import { defaultMedSectionBreak } from './types';
import { usePatientDetail } from '../../../context/PatientDetailContext';
import { v4 as uuid } from 'uuid';
import MedicationProfileDirtyProvider from './context/MedicationProfileDirtyProvider';
import CloseButton from './components/CloseButton';
import type { Medication } from '../../../lib/transferObjects/Medication';
import { useMedicationProfilesShared } from '../../components/ProfileBar/context/MedicationProfilesProvider';
import DuplicateMedProfileModal, {
  type DuplicationOptions,
} from './components/DuplicateMedProfileModal';

export const MedicationProfileCard = ({ onClose }: { onClose: () => void }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    medProfiles,
    isLoading: isLoadingMedProfiles,
    refresh,
  } = useMedicationProfilesShared();
  const { postDeletePatientMedProfiles, isDeleting } = useDeleteMedProfiles();
  const [currentTab, setCurrentTab] = React.useState<string>(
    MedicationProfileSteps.FULL_LIST,
  );
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [itemToDuplicateId, setItemToDuplicateId] = useState<string | null>(
    null,
  );
  const [shouldPrefillFromTimelineMeds, setShouldPrefillFromTimelineMeds] =
    useState(false);
  const [activeId, setActiveId] = useState('');
  const createMedProfileTempalte = useRef<Partial<MedProfile> | null>(null);
  const { medsForLatestEpisode } = usePatientDetail();
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onChangeTab = (tab: unknown, id?: string) => {
    if (tab === MedicationProfileSteps.FULL_LIST) {
      setActiveId('');
      refresh();
    } else if (tab === MedicationProfileSteps.MEDICATION_DETAIL && id) {
      setActiveId(id);
      requestAnimationFrame(scrollToTop);
    }
    // @ts-expect-error
    setCurrentTab(tab);
  };

  const onDuplicateMedProfile = (id: string) => {
    setItemToDuplicateId(id);
    setShowDuplicateModal(true);
  };

  const handleDuplicateConfirm = (options: DuplicationOptions) => {
    if (!itemToDuplicateId) return;

    const itemToDuplicate = medProfiles.find(
      (medProfile) => medProfile.id === itemToDuplicateId,
    );

    if (itemToDuplicate) {
      const duplicateMedication = (med: Partial<Medication>) => {
        const duplicatedMed: Partial<Medication> = {
          medId: med.medId,
          customMed: med.customMed,
          order: med.order,
          amt: med.amt
            ? {
                mpId: med.amt.mpId,
                mpuuId: med.amt.mpuuId,
                tpuuId: med.amt.tpuuId,
              }
            : undefined,
          relatedPathology: med.relatedPathology,
          medName: med.medName,
          brand: med.brand,
          subheading: med.subheading,
          furtherInformation: med.furtherInformation,
          indication: med.indication,
          indicationDescription: med.indicationDescription,
          isDeleted: med.isDeleted,
          regimen: med.regimen
            ? {
                isDoseSchedule: med.regimen.isDoseSchedule,
                doseSchedule: med.regimen.doseSchedule
                  ? {
                      isBreakfast: med.regimen.doseSchedule.isBreakfast,
                      breakfastNote: med.regimen.doseSchedule.breakfastNote,
                      isLunch: med.regimen.doseSchedule.isLunch,
                      lunchNote: med.regimen.doseSchedule.lunchNote,
                      isDinner: med.regimen.doseSchedule.isDinner,
                      dinnerNote: med.regimen.doseSchedule.dinnerNote,
                      isBed: med.regimen.doseSchedule.isBed,
                      bedNote: med.regimen.doseSchedule.bedNote,
                    }
                  : undefined,
                isPrn: med.regimen.isPrn,
                prn: med.regimen.prn
                  ? {
                      prnNote: med.regimen.prn.prnNote,
                    }
                  : undefined,
                isTaper: med.regimen.isTaper,
                taper: med.regimen.taper
                  ? {
                      taperTitle: med.regimen.taper.taperTitle,
                      dosingSchedule: med.regimen.taper.dosingSchedule,
                      taperHeaders: Array.isArray(
                        med.regimen.taper.taperHeaders,
                      )
                        ? [...med.regimen.taper.taperHeaders]
                        : undefined,
                      taperSchedule: Array.isArray(
                        med.regimen.taper.taperSchedule,
                      )
                        ? med.regimen.taper.taperSchedule.map((item) => ({
                            values: Array.isArray(item.values)
                              ? [...item.values]
                              : undefined,
                          }))
                        : undefined,
                    }
                  : undefined,
              }
            : undefined,
        };

        if (options.copyChangeType) {
          duplicatedMed.medChangeType = med.medChangeType;
          duplicatedMed.furtherDescriptionOfChange =
            med.furtherDescriptionOfChange;
        }

        return duplicatedMed;
      };

      const duplicatedProfile: Partial<MedProfile> = {
        profileStatus: MedicationProfileStates.IN_PROGRESS,
        pharmacist: itemToDuplicate.pharmacist,
        pdfOptions: itemToDuplicate.pdfOptions
          ? { ...itemToDuplicate.pdfOptions }
          : undefined,
      };

      // Copy med sections with cleaned medications
      if (itemToDuplicate.medSections) {
        duplicatedProfile.medSections = itemToDuplicate.medSections.map(
          (section) => ({
            order: section.order,
            medSectionBreak: section.medSectionBreak,
            medications: section.medications.map(duplicateMedication),
          }),
        );
      }

      // Copy letters if selected
      if (options.copyLetters) {
        if (itemToDuplicate.doctorLetter) {
          duplicatedProfile.doctorLetter = {
            ...itemToDuplicate.doctorLetter,
            summaryOfRecommendations: Array.isArray(
              itemToDuplicate.doctorLetter.summaryOfRecommendations,
            )
              ? [...itemToDuplicate.doctorLetter.summaryOfRecommendations]
              : undefined,
            monitoringRequired: Array.isArray(
              itemToDuplicate.doctorLetter.monitoringRequired,
            )
              ? [...itemToDuplicate.doctorLetter.monitoringRequired]
              : undefined,
          };
        }
        if (itemToDuplicate.pharmacistLetter) {
          duplicatedProfile.pharmacistLetter = {
            ...itemToDuplicate.pharmacistLetter,
            summaryOfRecommendations: Array.isArray(
              itemToDuplicate.pharmacistLetter.summaryOfRecommendations,
            )
              ? [...itemToDuplicate.pharmacistLetter.summaryOfRecommendations]
              : undefined,
          };
        }
      }

      // Copy appointments if selected
      if (options.copyAppointments && itemToDuplicate.appointments) {
        duplicatedProfile.appointments = itemToDuplicate.appointments.map(
          (appointment) => ({
            apptType: appointment.apptType,
            apptDate: appointment.apptDate,
            apptTime: appointment.apptTime,
          }),
        );
      }

      createMedProfileTempalte.current = duplicatedProfile;
      setShouldPrefillFromTimelineMeds(false);
      setCurrentTab(MedicationProfileSteps.MEDICATION_CREATE_DETAIL);
      requestAnimationFrame(scrollToTop);
    }

    setShowDuplicateModal(false);
    setItemToDuplicateId(null);
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
    setItemToDuplicateId(null);
  };

  const onCreateBlankMedProfile = () => {
    createMedProfileTempalte.current = {
      medSections: [
        {
          medSectionBreak: defaultMedSectionBreak,
          medications: Array.isArray(medsForLatestEpisode)
            ? medsForLatestEpisode.map((med) => {
                let tempMed: Medication = {
                  medId: uuid(),
                  customMed: true,
                  medName: med.medName,
                  orderStatusCode:
                    (med as any)?.orderStatusCode ??
                    (med as any)?.orderstatuscode ??
                    undefined,
                  furtherInformation:
                    (med as any)?.furtherInformation ??
                    (med as any)?.further_information ??
                    med.dosageInstruction ??
                    undefined,
                  subheading:
                    (med as any)?.subheading ??
                    (med as any)?.subHeading ??
                    undefined,
                };
                if (med.dosageInstruction) {
                  tempMed['regimen'] = {
                    isPrn: true,
                    prn: {
                      prnNote: med.dosageInstruction,
                    },
                  };
                }
                return tempMed;
              })
            : [],
        },
      ],
    };
    setShouldPrefillFromTimelineMeds(true);
    setCurrentTab(MedicationProfileSteps.MEDICATION_CREATE_DETAIL);
    requestAnimationFrame(scrollToTop);
  };

  const onDeleteMedProfile = async (id: string) => {
    const itemToDelete = medProfiles.find((medProfile) => medProfile.id === id);
    if (itemToDelete) {
      const { success } = await postDeletePatientMedProfiles(itemToDelete);
      if (!success) {
        throw new Error('delete medication profile failed');
      }
      refresh();
      return;
    }
    throw new Error('Medication profile not found');
  };

  const activeItem = useMemo(() => {
    if (activeId) {
      const foundItem = medProfiles.find(
        (medProfile) => medProfile.id === activeId,
      );
      return foundItem ?? null;
    }
    return null;
  }, [activeId, medProfiles]);

  return (
    <MedicationProfileDirtyProvider>
      <div className="medication-profile-modal-container" ref={containerRef}>
        <div
          style={{ display: 'flex', justifyContent: 'end', margin: '10px 5px' }}
        >
          <CloseButton onClick={onClose} />
        </div>
        <div className="medication-profile-modal-content">
          {currentTab === MedicationProfileSteps.FULL_LIST && (
            <MedicationProfileRecords
              onChangeTab={onChangeTab}
              medProfiles={medProfiles}
              onDeleteMedProfile={onDeleteMedProfile}
              onDuplicateMedProfile={onDuplicateMedProfile}
              onCreateBlankMedProfile={onCreateBlankMedProfile}
              isDeleting={isDeleting}
              isLoadingMedProfiles={isLoadingMedProfiles}
            />
          )}

          {currentTab === MedicationProfileSteps.MEDICATION_DETAIL && (
            <MedicationProfileDetail
              onChangeTab={onChangeTab}
              activeItem={activeItem}
            />
          )}
          {currentTab === MedicationProfileSteps.MEDICATION_CREATE_DETAIL && (
            <MedicationProfileDetail
              onChangeTab={onChangeTab}
              activeItem={createMedProfileTempalte.current}
              createMedProfile
              shouldPrefillFromTimelineMeds={shouldPrefillFromTimelineMeds}
            />
          )}
        </div>
      </div>
      <DuplicateMedProfileModal
        show={showDuplicateModal}
        onConfirm={handleDuplicateConfirm}
        onCancel={handleDuplicateCancel}
      />
    </MedicationProfileDirtyProvider>
  );
};
